import { useState } from 'react'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@/components/ui/modal'
import { useAuth } from '@/hooks/use-auth'
import { useVoidInvoice } from '@/hooks/use-billing'
import { getApiErrorMessage } from '@/lib/api'

export function VoidInvoiceModal({
  invoiceId,
  invoiceNumber,
  isCreditNote = false,
  onClose,
  onVoided,
}: {
  invoiceId: string
  invoiceNumber: string
  isCreditNote?: boolean
  onClose: () => void
  onVoided: () => void
}) {
  const { user } = useAuth()
  const isEstateManager = user?.role === 'estate_manager'
  const voidInvoice = useVoidInvoice()
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  async function handleConfirm() {
    setError(null)
    if (isEstateManager && !reason.trim()) {
      setError('A reason is required to request approval')
      return
    }
    try {
      const result = await voidInvoice.mutateAsync({
        id: invoiceId,
        reason: reason.trim() || undefined,
      })
      if (result.data.pendingApproval) {
        alert('Void requested — sent for committee/admin approval.')
      }
      onVoided()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to void invoice'))
    }
  }

  return (
    <Modal open onClose={onClose} labelledBy="void-invoice-title" panelClassName="sm:max-w-sm">
      <ModalHeader>
        <div className="flex items-start justify-between gap-4">
          <h2 id="void-invoice-title" className="font-serif text-xl text-ink">
            Void {invoiceNumber}?
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-ink"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>
      </ModalHeader>
      <ModalBody>
        <p className="font-sans text-sm text-muted-foreground">
          {isEstateManager
            ? `This will be sent to committee/admin for approval before it takes effect — the ${isCreditNote ? 'credit note' : 'invoice'} stays as-is until then.`
            : isCreditNote
              ? "This cancels the credit note permanently — the credit will no longer reduce the member's bills. This can't be undone."
              : "This cancels the invoice permanently — it will no longer count toward the member's balance. This can't be undone."}
        </p>
        {isEstateManager && (
          <div className="mt-4">
            <Input
              label="Reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. duplicate charge, wrong meter reading"
              required
            />
          </div>
        )}
        {error && (
          <p className="mt-3 font-sans text-xs text-destructive" role="alert">
            {error}
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <Button type="button" variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="button"
          variant="destructive"
          loading={voidInvoice.isPending}
          onClick={() => void handleConfirm()}
        >
          {isEstateManager ? 'Request void' : 'Void invoice'}
        </Button>
      </ModalFooter>
    </Modal>
  )
}
